import React, { useEffect } from 'react';
import { AppTheme, AppView } from './App';
import './ThemeSwitcher.css'; // We'll create this CSS file next

interface ThemeSwitcherProps {
  currentTheme: AppTheme;
  currentView: AppView;
  setTheme: (theme: AppTheme) => void;
  onCapture: () => void; // Add prop for capture function
  onAdjust: () => void; // Add prop for adjust function
  onShowWheelClick: () => void;
  onCopyLink: () => void; // Add prop for copy link function
  showCopyTooltip: boolean; // Add prop for tooltip visibility
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ currentTheme, currentView, setTheme, onCapture, onAdjust, onShowWheelClick, onCopyLink, showCopyTooltip }) => {
  // We'll simplify the switch to toggle between 'fikahjulet' (dark) and 'bw' (light)
  const isLight = currentTheme === 'bw';

  // Check URL for theme parameter on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const themeParam = queryParams.get('mode');
    
    if (themeParam) {
      if (themeParam.toLowerCase() === 'light' && currentTheme !== 'bw') {
        setTheme('bw');
      } else if (themeParam.toLowerCase() === 'dark' && currentTheme !== 'fikahjulet') {
        setTheme('fikahjulet');
      }
    }
  }, [currentTheme, setTheme]);

  const toggleTheme = () => {
    const newTheme = isLight ? 'fikahjulet' : 'bw';
    setTheme(newTheme);
    
    // Update URL with the new theme parameter
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('mode', newTheme === 'bw' ? 'light' : 'dark');
    
    // Preserve other parameters
    const newUrl = `${window.location.pathname}?${queryParams.toString().replace(/%2C/g, ',')}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  };

  // Sun Icon (for dark theme, switches to light)
  const SunIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" height="24"
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" /* Color controlled by CSS */
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="theme-icon"
    >
      <circle cx="12" cy="12" r="5"></circle>
      <line x1="12" y1="1" x2="12" y2="3"></line>
      <line x1="12" y1="21" x2="12" y2="23"></line>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
      <line x1="1" y1="12" x2="3" y2="12"></line>
      <line x1="21" y1="12" x2="23" y2="12"></line>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
    </svg>
  );

  // Moon Icon (for light theme, switches to dark)
  const MoonIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24"
      height="24"
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" /* Color controlled by CSS */
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="theme-icon"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
    </svg>
  );

  const CameraIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="theme-icon camera-icon" // Added camera-icon class
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
      <circle cx="12" cy="13" r="4"></circle>
    </svg>
  );

  // New Pen/Edit Icon
  const PenIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="theme-icon pen-icon" // Added pen-icon class
    >
      <path d="M12 20h9"></path>
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
    </svg>
  );

  // New Link Icon
  const LinkIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="theme-icon link-icon" // Added link-icon class
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
    </svg>
  );

  // New Wheel Icon (simplified)
  const WheelIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="theme-icon wheel-icon"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <circle cx="12" cy="12" r="3"></circle>
      <line x1="12" y1="2" x2="12" y2="9"></line>
      <line x1="12" y1="15" x2="12" y2="22"></line>
      <line x1="22" y1="12" x2="15" y2="12"></line>
      <line x1="9" y1="12" x2="2" y2="12"></line>
    </svg>
  );

  return (
    <div className="theme-controls-container"> {/* Container for all buttons */}
      <button
        className={`icon-button adjust-show-wheel-button ${isLight ? 'light' : 'dark'}`}
        onClick={currentView === 'wheel' ? onAdjust : onShowWheelClick}
        aria-label={currentView === 'wheel' ? 'Justera namn' : 'Visa Fikahjulet'}
      >
        {currentView === 'wheel' ? <PenIcon /> : <WheelIcon />}
      </button>
      <button
        className={`icon-button save-image-icon-button ${isLight ? 'light' : 'dark'}`}
        onClick={onCapture}
        aria-label="Spara som bild"
      >
        <CameraIcon />
      </button>
      {/* Copy Link Button and Tooltip Container */}
      <div className="copy-link-container"> {/* Wrap button and tooltip */}
        <button
          className={`icon-button copy-link-icon-button ${isLight ? 'light' : 'dark'}`}
          onClick={onCopyLink} // Use onCopyLink prop
          aria-label="Kopiera länk"
        >
          <LinkIcon />
        </button>
        {/* Tooltip */} 
        {showCopyTooltip && (
          <div className={`copy-link-tooltip ${isLight ? 'light' : 'dark'}`}>
            Länk kopierad!
          </div>
        )}
      </div>
      <button 
        className={`icon-button theme-switcher-button ${isLight ? 'light' : 'dark'}`}
        onClick={toggleTheme}
        aria-label={`Byt till ${isLight ? 'mörkt' : 'ljust'} läge`}
      >
        {isLight ? <MoonIcon /> : <SunIcon />}
      </button>
    </div>
  );
};

export default ThemeSwitcher; 